import { useParams, useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  FC,
  ReactNode,
  createContext,
  useContext,
  useMemo,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { State } from 'redux/initialState';
import { store } from 'redux/store';
import { useGetActualSociety } from 'hooks/useGetActualSociety';
import { useGetMenu } from 'modules/administratives/hooks/useGetMenu';

import menuTypes from 'constants/menuTypes';
import sizes from 'constants/sizes';
import { setModal } from 'modules/_shared/redux/modalActions';
import { Partner, TradingOffer } from 'types';
import { TradingOfferType, TradingOffersSummary } from 'types/TradingOffer';
import { TradingOfferOperation } from 'types/TradingOfferOperation';
import {
  EquityMarketContextType,
  MarketInfo,
  PartnerActiveOffers,
  PartnerOperations,
} from '../types/EquityMarketContextType';
import { equityMarketInitValue } from '../constants/EquityMarketInitValue';
import { useGetTradingOffers } from '../hooks/useGetTradingOffers';
import { getTradingOfferOperations } from '../redux/tradingOfferOperationActions';
import HowItWorksModal from '../modals/HowItWorksModal';

const getPartnerId = (
  party: string | Partial<Partner> | undefined
): string | null => {
  if (!party) return null;
  if (typeof party === 'string') return party;
  return (party as Partner)?._id ?? null;
};

export const EquityMarketContext = createContext<EquityMarketContextType>(
  equityMarketInitValue
);

const TRADING_OFFERS_PAGE_SIZE = 100;
const TRADING_OFFERS_FILTERS: string[] = [];

export const EquityMarketProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { societyId } = useParams<{
    societyId: string;
  }>();
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const partnerId = queryParams.get('partnerId');

  const actualSociety = useSelector(
    ({ society }: State) => society?.actualSociety
  );
  const user = useSelector(({ user }: State) => user);
  const isAdmin = useSelector(
    (state: State) => state.society?.role?.isAdmin ?? false
  );
  const isPartner = useSelector(
    (state: State) => state.society?.role?.isPartner ?? false
  );
  const tradingOffers = useSelector(
    ({ tradingOffers }: State) => (tradingOffers ?? []) as TradingOffer[]
  );

  const { partnerShares, referenceInfo } = equityMarketInitValue.data;
  const recentActivity = useMemo(() => [], []);
  const [tradingOfferOperations, setTradingOfferOperations] = useState<
    TradingOfferOperation[]
  >([]);

  useGetActualSociety(societyId);
  useGetMenu(menuTypes.NULL);

  useEffect(() => {
    if (!actualSociety || !societyId) return;

    const controller = new AbortController();

    const fetchOperations = async () => {
      try {
        const result = await store.dispatch(
          getTradingOfferOperations({ societyId })
        );
        if (!controller.signal.aborted && result?.data) {
          setTradingOfferOperations(result.data);
        }
      } catch {
        // Error handled by action
      }
    };

    fetchOperations();

    return () => controller.abort();
  }, [actualSociety, societyId]);

  const { isLoading: isTradingOffersLoading } = useGetTradingOffers({
    societyId: actualSociety ? societyId : undefined,
    page: 1,
    rowsPerPage: TRADING_OFFERS_PAGE_SIZE,
    filters: TRADING_OFFERS_FILTERS,
  });

  const isReadOnly = false;

  const isMarketClosed = useMemo(() => {
    const status = actualSociety?.configuration?.equityMarketStatus;
    return status === 'CLOSED' || status === 'UNAVAILABLE';
  }, [actualSociety?.configuration?.equityMarketStatus]);

  const sellOffers = useMemo(
    () => tradingOffers.filter((offer) => offer.type === TradingOfferType.SELL),
    [tradingOffers]
  );

  const buyOffers = useMemo(
    () => tradingOffers.filter((offer) => offer.type === TradingOfferType.BUY),
    [tradingOffers]
  );

  const calculateSummary = useCallback(
    (offers: TradingOffer[]): TradingOffersSummary => {
      const offersCount = offers.length;
      const sharesCount = offers.reduce(
        (acc, offer) => acc + offer.sharesCount.total,
        0
      );
      const totalAmount = offers.reduce(
        (acc, offer) => acc + offer.sharesCount.total * offer.sharePrice,
        0
      );

      return { offersCount, sharesCount, totalAmount };
    },
    []
  );

  const sellOffersSummary = useMemo(
    () => calculateSummary(sellOffers),
    [sellOffers, calculateSummary]
  );

  const buyOffersSummary = useMemo(
    () => calculateSummary(buyOffers),
    [buyOffers, calculateSummary]
  );

  const marketInfo: MarketInfo = useMemo(() => {
    if (!sellOffers && !buyOffers) return equityMarketInitValue.data.marketInfo;

    const totalOffers = (sellOffers?.length ?? 0) + (buyOffers?.length ?? 0);
    const sellCount = sellOffers?.length ?? 0;
    const buyCount = buyOffers?.length ?? 0;
    const sellAmount = sellOffersSummary?.totalAmount ?? 0;
    const buyAmount = buyOffersSummary?.totalAmount ?? 0;

    return { totalOffers, sellCount, buyCount, sellAmount, buyAmount };
  }, [sellOffers, buyOffers, sellOffersSummary, buyOffersSummary]);

  const partnerActiveOffers: PartnerActiveOffers = useMemo(() => {
    if (!sellOffers && !buyOffers)
      return equityMarketInitValue.data.partnerActiveOffers;

    const filteredSellOffers = partnerId
      ? sellOffers?.filter(
          (offer) => (offer.partner as Partner)._id === partnerId
        )
      : sellOffers;

    const filteredBuyOffers = partnerId
      ? buyOffers?.filter(
          (offer) => (offer.partner as Partner)._id === partnerId
        )
      : buyOffers;

    const total =
      (filteredSellOffers?.length ?? 0) + (filteredBuyOffers?.length ?? 0);
    const sellCount = filteredSellOffers?.length ?? 0;
    const buyCount = filteredBuyOffers?.length ?? 0;

    const sellAmount =
      filteredSellOffers?.reduce(
        (acc, offer) => acc + offer.sharesCount.total * offer.sharePrice,
        0
      ) ?? 0;
    const buyAmount =
      filteredBuyOffers?.reduce(
        (acc, offer) => acc + offer.sharesCount.total * offer.sharePrice,
        0
      ) ?? 0;

    return { total, sellCount, buyCount, sellAmount, buyAmount };
  }, [sellOffers, buyOffers, partnerId]);

  const userId = user?._id ?? '';
  const partnerOperations: PartnerOperations = useMemo(() => {
    const userOps = tradingOfferOperations.filter((op) => {
      const buyerId = getPartnerId(op.buyer);
      const sellerId = getPartnerId(op.seller);
      return buyerId === userId || sellerId === userId;
    });

    const sellCount = userOps.filter(
      (op) => getPartnerId(op.seller) === userId
    ).length;
    const buyCount = userOps.filter(
      (op) => getPartnerId(op.buyer) === userId
    ).length;
    const sellAmount = userOps
      .filter((op) => getPartnerId(op.seller) === userId)
      .reduce((acc, op) => acc + (op.totalPrice ?? 0), 0);
    const buyAmount = userOps
      .filter((op) => getPartnerId(op.buyer) === userId)
      .reduce((acc, op) => acc + (op.totalPrice ?? 0), 0);

    return {
      total: userOps.length,
      sellCount,
      buyCount,
      sellAmount,
      buyAmount,
    };
  }, [tradingOfferOperations, userId]);

  const partnerQuery = partnerId ? `?partnerId=${partnerId}` : '';

  const handleCreateSellOffer = useCallback(() => {
    history.push(
      partnerId
        ? `/crear-oferta-venta/${societyId}/${partnerId}`
        : `/crear-oferta-venta/${societyId}`
    );
  }, [history, societyId, partnerId]);

  const handleCreateBuyOffer = useCallback(() => {
    history.push(
      partnerId
        ? `/crear-oferta-compra/${societyId}/${partnerId}`
        : `/crear-oferta-compra/${societyId}`
    );
  }, [history, societyId, partnerId]);

  const handleViewSellOffers = useCallback(() => {
    history.push(`/ofertas-venta/${societyId}${partnerQuery}`);
  }, [history, societyId, partnerQuery]);

  const handleViewBuyOffers = useCallback(() => {
    history.push(`/ofertas-compra/${societyId}${partnerQuery}`);
  }, [history, societyId, partnerQuery]);

  const handleViewOfferDetail = useCallback(
    (offerId: string) => {
      history.push(`/detalle-oferta/${societyId}/${offerId}${partnerQuery}`);
    },
    [history, societyId, partnerQuery]
  );

  const handleViewMyShares = useCallback(() => {
    history.push(`/mis-acciones/${societyId}${partnerQuery}`);
  }, [history, societyId, partnerQuery]);

  const handleViewMyOffers = useCallback(() => {
    history.push(`/ofertas/${societyId}/${user._id}${partnerQuery}`);
  }, [history, societyId, user, partnerQuery]);

  const handleViewMyOperations = useCallback(() => {
    history.push(`/mis-operaciones/${societyId}${partnerQuery}`);
  }, [history, societyId, partnerQuery]);

  const handleOpenHowItWorks = useCallback(() => {
    dispatch(
      setModal(<HowItWorksModal size={sizes.LG} societyId={societyId} />)
    );
  }, [dispatch, societyId]);

  const contextValue = useMemo(
    () => ({
      data: {
        societyId: societyId || '',
        societyName: actualSociety?.name || '',
        partnerShares,
        partnerActiveOffers,
        partnerOperations,
        marketInfo,
        sellOffers,
        buyOffers,
        sellOffersSummary,
        buyOffersSummary,
        recentActivity,
        referenceInfo,
        isAdmin,
        isPartner,
        isReadOnly,
        isLoading: isTradingOffersLoading || !actualSociety,
        isMarketClosed,
      },
      actions: {
        handleCreateSellOffer,
        handleCreateBuyOffer,
        handleViewSellOffers,
        handleViewBuyOffers,
        handleViewOfferDetail,
        handleViewMyShares,
        handleViewMyOffers,
        handleViewMyOperations,
        handleOpenHowItWorks,
      },
    }),
    [
      societyId,
      actualSociety,
      partnerShares,
      partnerActiveOffers,
      partnerOperations,
      marketInfo,
      sellOffers,
      buyOffers,
      sellOffersSummary,
      buyOffersSummary,
      recentActivity,
      referenceInfo,
      isAdmin,
      isPartner,
      isReadOnly,
      isTradingOffersLoading,
      isMarketClosed,
      handleCreateSellOffer,
      handleCreateBuyOffer,
      handleViewSellOffers,
      handleViewBuyOffers,
      handleViewOfferDetail,
      handleViewMyShares,
      handleViewMyOffers,
      handleViewMyOperations,
      handleOpenHowItWorks,
    ]
  );

  return (
    <EquityMarketContext.Provider value={contextValue}>
      {children}
    </EquityMarketContext.Provider>
  );
};

export const useEquityMarket = () => {
  const context = useContext(EquityMarketContext);
  if (!context) {
    throw new Error(
      'useEquityMarket must be used within an EquityMarketProvider'
    );
  }
  return context;
};
